import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";

const HOUR_LABELS = [
  "12 AM",
  "1 AM",
  "2 AM",
  "3 AM",
  "4 AM",
  "5 AM",
  "6 AM",
  "7 AM",
  "8 AM",
  "9 AM",
  "10 AM",
  "11 AM",
  "12 PM",
  "1 PM",
  "2 PM",
  "3 PM",
  "4 PM",
  "5 PM",
  "6 PM",
  "7 PM",
  "8 PM",
  "9 PM",
  "10 PM",
  "11 PM",
];

class AvailableDriversService {
  static async getAvailableDrivers(params = {}) {
    try {
      const queryParams = {};
      if (params.day) queryParams.day = params.day;
      if (params.pincode && params.pincode.length === 6) {
        queryParams.pincode = params.pincode;
      }
      if (params.status && params.status !== "both") {
        queryParams.status = params.status;
      }

      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString
        ? `${API.ADMIN.AVAILABLE_DRIVERS}?${queryString}`
        : API.ADMIN.AVAILABLE_DRIVERS;

      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching available drivers:", error);
      toast.error("Failed to fetch available drivers");
      throw error;
    }
  }
  static async getAvailableDriversForCell({ day, pincode, hour, status }) {
    try {
      const queryParams = {
        day,
        pincode,
        hour: String(hour),
      };
      if (status && status !== "both") {
        queryParams.status = status;
      }

      const queryString = new URLSearchParams(queryParams).toString();

      const response = await axiosInstance.get(
        `${API.ADMIN.AVAILABLE_DRIVERS_CELL}?${queryString}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching available drivers for cell:", error);
      toast.error("Failed to fetch driver list");
      throw error;
    }
  }

  static escapeCsvValue(value) {
    const str = value == null ? "" : String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }

  static exportPincodeGridCsv({ rows = [], evaluationDay = "", filters = {} }) {
    if (!rows.length) {
      toast.error("No data to export");
      return;
    }

    const selectedHourIndex =
      filters.startTime && HOUR_LABELS.includes(filters.startTime)
        ? HOUR_LABELS.indexOf(filters.startTime)
        : -1;

    const headers =
      selectedHourIndex >= 0
        ? ["Pin Code", HOUR_LABELS[selectedHourIndex]]
        : ["Pin Code", ...HOUR_LABELS, "Row Total"];

    const metaLines = [
      `# Evaluation Day: ${evaluationDay || filters.startDay || "N/A"}`,
      `# Time Period: ${filters.startTime || "All"}`,
      `# Status: ${filters.status || "both"}`,
      `# Pincode: ${filters.pincode?.length === 6 ? filters.pincode : "All"}`,
    ];

    const dataRows = rows.map((row) => {
      const hourlyCounts = Array.isArray(row.hourlyCounts) ? row.hourlyCounts : [];
      if (selectedHourIndex >= 0) {
        return [row.pincode, hourlyCounts[selectedHourIndex] ?? 0];
      }
      const rowTotal = hourlyCounts.reduce((sum, count) => sum + (Number(count) || 0), 0);
      return [
        row.pincode,
        ...HOUR_LABELS.map((_, index) => hourlyCounts[index] ?? 0),
        rowTotal,
      ];
    });

    const csvLines = [
      ...metaLines,
      headers.map((h) => this.escapeCsvValue(h)).join(","),
      ...dataRows.map((row) => row.map((cell) => this.escapeCsvValue(cell)).join(",")),
    ];

    const csvContent = csvLines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    const dayPart = (evaluationDay || filters.startDay || "day").replace(/\s+/g, "-");
    const statusPart = filters.status || "both";
    const timePart =
      selectedHourIndex >= 0
        ? `-${filters.startTime.replace(/\s+/g, "-")}`
        : "";
    const datePart = new Date().toISOString().split("T")[0];
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `available-drivers-${dayPart}-${statusPart}${timePart}-${datePart}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${rows.length} pincode row(s)`);
  }
}

export default AvailableDriversService;
